import { useCallback, useMemo, useState } from "react";
import { useFormik } from "formik";
import { DateTime } from "luxon";

import { ArrowLeftIcon } from "../../../assets/icons";
import {
  LoanProductType,
  LoanVendorType,
  MediaPurpose,
  MediaType,
} from "../../../hooks/api/types";
import { useLoanDashboard } from "../../../contexts";
import DrawdownForm from "./DrawdownForm";
import {
  DrawdownFields,
  getDrawdownValidationSchema,
} from "../../LoanCalculator/service";
import {
  asArray,
  asRecord,
  BulkParseErrorRow,
  BulkParseExtracted,
  BulkParseState,
  InvoiceEntryModeType,
  toNumberOrUndefined,
  toRowNumberOrUndefined,
  toStringOrUndefined,
} from "./service";
import { withProfitRateTerminology } from "../../../utils";
import {
  useBulkParseDrawdownTemplate,
  useCreateDrawdown,
  useMedia,
} from "../../../hooks";
import { useLoanProductConfigFlags } from "../../../hooks/useLoanProductConfigFlags";
import { UploadMediaArgs } from "../../../hooks/useMedia";
import {
  DEFAULT_CURRENCY,
  BulkParseStatus,
  InvoiceEntryMode,
} from "./constants";

async function handleLocInvoiceFiles(
  loanApplicationId: string,
  invoiceFiles: File[],
  upload: (payload: UploadMediaArgs) => Promise<string>,
  setUploading?: React.Dispatch<React.SetStateAction<boolean>>,
  purpose: MediaPurpose = MediaPurpose.DRAWDOWN_INVOICE
): Promise<string[]> {
  setUploading && setUploading(true);

  const invoiceIds = await Promise.all(
    invoiceFiles.map(async (file) => {
      const id = await upload({
        file,
        type: MediaType.DOCUMENT,
        purpose,
        loanApplicationId,
      });
      return id;
    })
  );

  setUploading && setUploading(false);
  return invoiceIds;
}

export const Drawdown = () => {
  const [uploading, setUploading] = useState(false);
  const [bulkParsing, setBulkParsing] = useState(false);
  const [bulkParseState, setBulkParseState] = useState<BulkParseState>({
    status: BulkParseStatus.IDLE,
  });
  const { upload } = useMedia();

  const {
    actions: { update },
    state: {
      currentLoanApplication,
      currentLoanApplicationId: loanApplicationId,
      preselectedDrawdownVendor,
    },
  } = useLoanDashboard();

  const { mutateAsync: createDrawdown, isLoading } = useCreateDrawdown();
  const { mutateAsync: parseBulkTemplate } = useBulkParseDrawdownTemplate();
  const [error, setError] = useState<string | undefined>(undefined);
  /** Overall facility credit limit only — buyer/supplier sub-limit is display-only. */
  const availableLimit =
    currentLoanApplication?.creditLine?.availableAmount || 0;
  const productType =
    currentLoanApplication?.loanProduct?.type ||
    currentLoanApplication?.loanType;
  const { useProfitRateTerminology: shouldUseProfitRateTerminology } =
    useLoanProductConfigFlags(productType as LoanProductType | undefined);
  const isPayablesFacility =
    productType === LoanProductType.LOC_PAYABLE_FINANCING;
  const [invoiceEntryMode, setInvoiceEntryMode] =
    useState<InvoiceEntryModeType>(InvoiceEntryMode.MANUAL);
  const validationSchema = useMemo(
    () =>
      getDrawdownValidationSchema({
        vendorType: isPayablesFacility
          ? LoanVendorType.SUPPLIER
          : LoanVendorType.BUYER,
        availableLimit,
        requireCurrency: Boolean(isPayablesFacility),
        maxDiscountLimit: currentLoanApplication?.creditLine?.advancePercentage,
        isPayablesFacility,
        maxInvoices:
          invoiceEntryMode === InvoiceEntryMode.MANUAL ? 10 : undefined,
      }),
    [
      availableLimit,
      currentLoanApplication?.creditLine?.advancePercentage,
      invoiceEntryMode,
      isPayablesFacility,
    ]
  );
  const formik = useFormik<DrawdownFields>({
    initialValues: {
      vendorId: preselectedDrawdownVendor?.vendorId ?? "",
      currency: DEFAULT_CURRENCY,
      invoiceFiles: [],
      invoices: [
        {
          invoiceNumber: "",
          invoiceAmount: undefined,
          requiredAmount: undefined,
          issuedDate: undefined,
          dueDate: undefined,
        },
      ],
      totalAmount: 0,
      numberOfInvoices: 0,
      // AED conversion fields
      requestedAmountInAED: undefined,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const uploadedFileIds = await handleLocInvoiceFiles(
          loanApplicationId,
          values.invoiceFiles,
          upload,
          setUploading
        );

        const { totalAmount, numberOfInvoices, ...drawdownFieldsToSubmit } =
          values;

        const formatDate = (value?: string | Date) => {
          if (!value) return undefined;
          const d = value instanceof Date ? value : new Date(value);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        const normalizedInvoices = values.invoices.map((invoice) => ({
          invoiceNumber: invoice.invoiceNumber,
          invoiceAmount:
            invoice.invoiceAmount !== undefined &&
            invoice.invoiceAmount !== null
              ? Number(invoice.invoiceAmount)
              : undefined,
          requiredAmount:
            invoice.requiredAmount !== undefined &&
            invoice.requiredAmount !== null
              ? Number(invoice.requiredAmount)
              : undefined,
          issuedDate: formatDate(invoice?.issuedDate),
          dueDate: formatDate(invoice?.dueDate),
        }));

        // Build the drawdown fields with exchange rate data included
        const drawdownFields = {
          ...drawdownFieldsToSubmit,
          invoiceFiles: uploadedFileIds,
          invoices: normalizedInvoices,
          // Include exchange rate data for non-AED currencies
          ...(values.currency !== DEFAULT_CURRENCY && {
            requestedAmountInAED: values.requestedAmountInAED, // Tentative AED amount
          }),
        };

        await createDrawdown({ loanApplicationId, drawdownFields });
        update({
          currentLoanApplicationId: loanApplicationId,
          showDrawdownForm: false,
          preselectedDrawdownVendor: null,
          showDrawdownSuccess: true,
        });
      } catch (error: unknown) {
        const message =
          asRecord(error)?.message &&
          typeof asRecord(error)?.message === "string"
            ? (asRecord(error)?.message as string)
            : "Something went wrong, refresh and try again!";
        setError(message);
      }
    },
  });

  const parseDateValue = (value: unknown): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value !== "string") return undefined;

    const isoParsed = DateTime.fromISO(value);
    if (isoParsed.isValid) return isoParsed.toJSDate();

    const formattedParsed = DateTime.fromFormat(value, "d LLLL, yyyy");
    if (formattedParsed.isValid) return formattedParsed.toJSDate();

    const slashFormattedParsed = DateTime.fromFormat(value, "d/M/yyyy");
    if (slashFormattedParsed.isValid) return slashFormattedParsed.toJSDate();

    return undefined;
  };

  const tryExtractBulkParse = (payload: unknown): BulkParseExtracted => {
    const payloadObj = asRecord(payload) ?? {};
    const response = asRecord(payloadObj.response);
    const responseData = asRecord(response?.data);
    const source =
      asRecord(responseData?.data) ??
      responseData ??
      asRecord(payloadObj.data) ??
      payloadObj;
    const sourceData = asRecord(source.data);

    const totalAmount =
      toNumberOrUndefined(source.totalAmount) ??
      toNumberOrUndefined(source.tentativeTotalAmount) ??
      toNumberOrUndefined(source.tentativeDrawdownAmount) ??
      toNumberOrUndefined(source.totalDrawdownAmount);

    const successesRaw =
      source.successes ??
      source.successRows ??
      source.success ??
      sourceData?.successes ??
      sourceData?.successRows ??
      sourceData?.success;

    const errorsRaw =
      source.errors ??
      source.errorList ??
      source.errorRows ??
      sourceData?.errors ??
      sourceData?.errorList ??
      sourceData?.errorRows;

    const payloadSuccessRows = asArray<Record<string, unknown>>(
      asRecord(payloadObj.success)?.rows
    );
    const payloadErrorRows = asArray<Record<string, unknown>>(
      asRecord(payloadObj.error)?.rows
    );

    const successesArray = asArray<Record<string, unknown>>(successesRaw);
    const errorsArray = asArray<Record<string, unknown>>(errorsRaw);

    const finalSuccessesArray =
      successesArray.length > 0 ? successesArray : payloadSuccessRows;
    const finalErrorsArray =
      errorsArray.length > 0 ? errorsArray : payloadErrorRows;

    const errors =
      finalErrorsArray.length > 0
        ? finalErrorsArray.map((e, idx) => ({
            row:
              toRowNumberOrUndefined(e.row) ??
              toRowNumberOrUndefined(e.rowNumber) ??
              toRowNumberOrUndefined(e.line) ??
              toRowNumberOrUndefined(e.lineNumber) ??
              toRowNumberOrUndefined(e.index) ??
              idx + 2,
            message:
              toStringOrUndefined(e.message) ??
              toStringOrUndefined(e.error) ??
              toStringOrUndefined(e.detail) ??
              toStringOrUndefined(e.msg),
          }))
        : undefined;

    const fallbackMessage =
      toStringOrUndefined(source.message) ??
      toStringOrUndefined(source.error) ??
      toStringOrUndefined(source.details) ??
      toStringOrUndefined(payloadObj.message) ??
      toStringOrUndefined(payloadObj.error) ??
      toStringOrUndefined(responseData?.message) ??
      toStringOrUndefined(responseData?.error);

    let normalizedErrors: BulkParseErrorRow[] | undefined;
    if (errors && errors.length > 0) {
      normalizedErrors = errors;
    } else if (fallbackMessage) {
      normalizedErrors = [{ message: fallbackMessage }];
    } else {
      normalizedErrors = undefined;
    }

    const successes =
      finalSuccessesArray.length > 0
        ? finalSuccessesArray.map((r) => ({
            invoiceNo:
              toStringOrUndefined(r.invoiceNo) ??
              toStringOrUndefined(r.invoiceNumber) ??
              toStringOrUndefined(r.invoice_no),
            invoiceAmount: toNumberOrUndefined(r.invoiceAmount),
            requestedAmount:
              toNumberOrUndefined(r.requestedAmount) ??
              toNumberOrUndefined(r.requiredAmount),
            issuedDate:
              toStringOrUndefined(r.issuedDate) ??
              toStringOrUndefined(r.invoiceIssuedDate),
            dueDate:
              toStringOrUndefined(r.dueDate) ??
              toStringOrUndefined(r.invoiceDueDate),
          }))
        : undefined;

    const numberOfInvoices =
      toNumberOrUndefined(source.numberOfInvoices) ??
      toNumberOrUndefined(source.parsedInvoiceCount) ??
      finalSuccessesArray.length;

    return {
      totalAmount,
      numberOfInvoices,
      errors: normalizedErrors,
      successes,
    };
  };

  const handleBulkParseTemplate = async (templateFile: File) => {
    setBulkParsing(true);
    setBulkParseState({ status: BulkParseStatus.EXTRACTING });
    try {
      if (!formik.values.vendorId) {
        setBulkParseState({
          status: BulkParseStatus.ERRORS,
          errors: [
            {
              message:
                "Please select a vendor before uploading the bulk template.",
            },
          ],
        });
        return;
      }

      const uploadedFileIds = await handleLocInvoiceFiles(
        loanApplicationId,
        [templateFile],
        upload,
        undefined,
        MediaPurpose.DRAWDOWN_INVOICE
      );

      const resp = await parseBulkTemplate({
        loanApplicationId,
        body: {
          vendorId: formik.values.vendorId,
          currency: formik.values.currency,
          templateFileId: uploadedFileIds[0],
        },
      });

      const extracted = tryExtractBulkParse(resp);

      if (extracted?.errors && extracted.errors.length > 0) {
        setBulkParseState({
          status: BulkParseStatus.ERRORS,
          totalAmount: extracted.totalAmount,
          numberOfInvoices: extracted.numberOfInvoices,
          errors: extracted.errors,
          successes: extracted.successes,
        });
        return;
      }

      const successInvoices =
        extracted?.successes?.map((row) => ({
          invoiceNumber: row.invoiceNo ? String(row.invoiceNo) : "",
          invoiceAmount: row.invoiceAmount ?? undefined,
          requiredAmount: row.requestedAmount ?? undefined,
          issuedDate: parseDateValue(row.issuedDate),
          dueDate: parseDateValue(row.dueDate),
        })) ?? [];

      setBulkParseState({
        status: BulkParseStatus.SUCCESS,
        totalAmount: extracted.totalAmount,
        numberOfInvoices: extracted.numberOfInvoices,
        errors: undefined,
        successes: extracted.successes,
      });

      formik.setFieldValue("invoices", successInvoices);
    } catch (e: unknown) {
      const extracted = tryExtractBulkParse(e);

      setBulkParseState({
        status: BulkParseStatus.ERRORS,
        totalAmount: extracted.totalAmount,
        numberOfInvoices: extracted.numberOfInvoices,
        errors: extracted.errors,
        successes: extracted.successes,
      });
    } finally {
      setBulkParsing(false);
    }
  };

  const handleClearBulkTemplate = useCallback(() => {
    setBulkParseState({ status: BulkParseStatus.IDLE });
    // Reset invoices so manual validation stays consistent until a new template is parsed.
    formik.setFieldValue("invoices", [
      {
        invoiceNumber: "",
        invoiceAmount: undefined,
        requiredAmount: undefined,
        issuedDate: undefined,
        dueDate: undefined,
      },
    ] as DrawdownFields["invoices"]);
  }, [formik.setFieldValue]);
  const previousStep = () =>
    update({
      currentLoanApplicationId: loanApplicationId,
      showDrawdownForm: false,
      preselectedDrawdownVendor: null,
    });

  const currencyCode =
    currentLoanApplication?.creditLine?.currency?.toUpperCase() || "AED";

  const creditLineInterestRate =
    currentLoanApplication?.creditLine?.interestRate;

  return (
    <div className="cx-background-radial-gradient cx-w-full cx-min-h-screen">
      <div className="cx-max-w-[960px] cx-mx-auto cx-px-4 sm:cx-px-6">
        <div className="cx-flex cx-flex-col lg:cx-flex-row lg:cx-items-center cx-gap-4 cx-mb-8 cx-pt-8">
          <button
            className="cx-inline-flex cx-items-center cx-bg-blue-600 cx-rounded-full cx-text-white cx-py-2 cx-pr-4 cx-shadow-modal cx-w-fit"
            onClick={previousStep}
          >
            <ArrowLeftIcon className="cx-w-5 cx-h-5 cx-mx-2 cx-text-white" />
            Back
          </button>
          <div className="cx-flex cx-flex-col cx-flex-1">
            <div className="cx-flex cx-items-baseline cx-gap-2 cx-flex-wrap">
              <h1 className="cx-text-2xl cx-font-semibold">
                Apply for new drawdown
              </h1>
              <span className="cx-inline-block cx-w-1.5 cx-h-1.5 cx-rounded-full cx-bg-gray-300" />
              {creditLineInterestRate !== undefined && (
                <span className="cx-text-[14px] cx-font-medium cx-text-gray-500">
                  {withProfitRateTerminology(
                    `Interest rate ${creditLineInterestRate}% per month`,
                    shouldUseProfitRateTerminology
                  )}
                </span>
              )}
            </div>
          </div>
          <div className="cx-flex cx-items-center cx-gap-1 cx-bg-white cx-rounded-full cx-px-2 cx-border cx-border-gray-100 cx-shadow-sm max-w-[200px]">
            <div className="cx-flex cx-items-center cx-gap-1">
              <span className="cx-text-sm cx-text-gray-500">Credit limit</span>
              <span className="cx-inline-flex cx-items-center cx-justify-center cx-w-1 cx-h-1 cx-bg-gray-300 cx-rounded-full" />
            </div>
            <span className="cx-flex cx-items-center cx-text-[#088A86]">
              {currencyCode === "AED" && (
                <div className="cx-font-aed-font"> D </div>
              )}
              {currencyCode !== "AED" && (
                <span className="cx-text-text-brand cx-text-sm cx-mr-1">
                  {currencyCode}
                </span>
              )}
              {availableLimit.toLocaleString()}
            </span>
          </div>
        </div>

        <DrawdownForm
          formik={formik}
          availableLimit={availableLimit}
          maxDiscountLimit={
            currentLoanApplication?.creditLine?.advancePercentage ?? 100
          }
          loanApplicationId={loanApplicationId}
          previousStep={previousStep}
          loading={uploading || isLoading}
          error={error}
          bulkParseState={bulkParseState}
          bulkParsing={bulkParsing}
          invoiceEntryMode={invoiceEntryMode}
          onInvoiceEntryModeChange={setInvoiceEntryMode}
          onBulkParseTemplate={handleBulkParseTemplate}
          onBulkClearTemplate={handleClearBulkTemplate}
        />
      </div>
    </div>
  );
};

export default Drawdown;
